import React, { useState } from 'react';
import styles from './DocumentationFilePickerItem.module.scss';
import { DocumentationFile } from '../../../../graphql/graphql';
import { FormattedDateTime } from '../../../../Components/FormattedDate';
import { Theme } from '../../../../Enums/Theme.enum';
import Button from '../../../../Components/Button/Button';
import MediaViewerModal from '../../../../Components/MediaViewer/MediaViewerModal';
import { prepareDocumentationFileForMediaViewer } from '../../../Ecard/Services/eCardFile.service';
import { useAppContext } from '../../../../Services/AppContext';
import AntivirusCheck from '../../../../Components/Base/Loading/AntivirusCheck';
import useDocumentationFileTransfer from '../../../Ecard/Hooks/useDocumentationFileTransfer';
import { ECardType } from '../../../Ecard/Enums/ECardType.enum';
import useTranslation from '../../../../Hooks/useTranslation';
import { AntivirusResult } from '../../../../Enums/AntivirusResult.enum';
import useLoading from '../../../Base/Hooks/useLoading';
import LinkButton from '../../../../Components/Button/LinkButton';

interface Props {
	onAdd: (file: DocumentationFile) => Promise<void>;
	onRemove: (file: DocumentationFile) => Promise<void>;
	isAdded: boolean;
	item: DocumentationFile;
}

const DocumentationFilePickerItem: React.FC<Props> = ({
	item,
	isAdded,
	onAdd,
	onRemove,
}) => {
	const { isLoading, setIsLoading } = useLoading();
	const [isFileOpen, setIsFileOpen] = useState(false);
	const [isSelected, setIsSelected] = useState(isAdded);
	const { transfersRecords } = useDocumentationFileTransfer(
		item,
		ECardType.HEALTH_DOCUMENTATION,
	);
	const { t } = useTranslation();
	const { socketUrl } = useAppContext();

	const handleClick = async () => {
		setIsLoading(true);

		if (isSelected) {
			await onRemove(item);
		} else {
			await onAdd(item);
		}

		setIsSelected(prev => !prev);
		setIsLoading(false);
	};

	return (
		<>
			<div className={styles.wrapper}>
				<div className={styles.date}>
					<FormattedDateTime datetime={item.createdAt} />
				</div>
				<div className={styles.file}>
					{item.avResult === AntivirusResult.CLEAN && (
						<div onClick={() => setIsFileOpen(true)} className={styles.name}>
							{item.displayName}
						</div>
					)}
					{item.avResult !== AntivirusResult.CLEAN && (
						<div className={styles.name}>{item.displayName}</div>
					)}
					{((item.createdBy && !item.createdBy.isPatient) ||
						item.workplace) && (
						<div className={styles.savedBy}>
							{t('base.shared.inserted')}: {item.createdBy?.profile?.fullName}
							{item.workplace && item.createdBy && ', '}
							{item.workplace && t('base.shared.workplace')}{' '}
							{item.workplace?.name}
						</div>
					)}
					{item.createdBy && item.createdBy.isPatient && (
						<div className={styles.savedBy}>
							{t('base.shared.inserted')}: {item.createdBy?.profile?.fullName}
						</div>
					)}
					{transfersRecords?.map((transfer, key) => (
						<div className={styles.savedBy} key={key}>
							<span>{transfer.direction}</span> {transfer.text}
						</div>
					))}
				</div>
				{item.avResult === AntivirusResult.CLEAN && (
					<div className={styles.itemControls}>
						<Button
							onClick={handleClick}
							theme={isSelected ? Theme.RED : Theme.GREEN}
							title={
								isSelected
									? 'component.documentationFilePickerItem.remove'
									: 'component.documentationFilePickerItem.connect'
							}
							disabled={isLoading}
						/>
						<LinkButton
							onClick={() => setIsFileOpen(true)}
							theme={Theme.BLUE}
							title='base.show'
							firstUpper
						/>
					</div>
				)}
				{item.avResult !== AntivirusResult.CLEAN && (
					<div className={styles.itemControls}>
						<AntivirusCheck inRow={true} />
					</div>
				)}
			</div>
			{isFileOpen && (
				<MediaViewerModal
					opened
					onClose={() => setIsFileOpen(false)}
					file={prepareDocumentationFileForMediaViewer(item, socketUrl)}
				/>
			)}
		</>
	);
};

export default DocumentationFilePickerItem;
